import React, { useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Camera, Sparkles, Upload, Loader2, RefreshCw, Heart, X, Wand2, Clock, Flame, ArrowRight, Check } from 'lucide-react';
import { toast } from 'sonner';
import { suggestFromPhoto, savePreference, saveMeal } from '../lib/api';

// Individual swipe card — draggable via pointer events
const SwipeCard = ({ item, onSwipe, index, total }) => {
  const ref = useRef(null);
  const start = useRef({ x: 0, y: 0, active: false });
  const [dx, setDx] = useState(0);
  const [dy, setDy] = useState(0);
  const [leaving, setLeaving] = useState(null); // 'like' | 'dislike'
  const [showSteps, setShowSteps] = useState(false);

  const onDown = (e) => {
    if (leaving) return;
    start.current = { x: e.clientX, y: e.clientY, active: true };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onMove = (e) => {
    if (!start.current.active) return;
    setDx(e.clientX - start.current.x);
    setDy((e.clientY - start.current.y) * 0.5);
  };
  const onUp = () => {
    if (!start.current.active) return;
    start.current.active = false;
    const threshold = 110;
    if (dx > threshold) fling('like');
    else if (dx < -threshold) fling('dislike');
    else { setDx(0); setDy(0); }
  };
  const fling = (dir) => {
    setLeaving(dir);
    setDx(dir === 'like' ? 800 : -800);
    setTimeout(() => onSwipe(dir), 260);
  };

  const rotation = dx / 20;
  const likeOpacity = Math.max(0, Math.min(1, dx / 120));
  const dislikeOpacity = Math.max(0, Math.min(1, -dx / 120));
  const offset = (total - 1 - index) * 6; // stack effect
  const scale = 1 - (total - 1 - index) * 0.03;

  return (
    <div
      ref={ref}
      className="absolute inset-0 select-none touch-none cursor-grab active:cursor-grabbing"
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
      style={{
        transform: `translate(${dx}px, ${dy + offset}px) rotate(${rotation}deg) scale(${scale})`,
        transition: start.current.active ? 'none' : 'transform 260ms cubic-bezier(.2,.8,.2,1)',
        zIndex: index + 1,
      }}
    >
      <div className="w-full h-full bg-white rounded-3xl border border-emerald-950/5 shadow-xl overflow-hidden flex flex-col">
        <div className="p-5 pb-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-display font-bold text-emerald-950 text-xl leading-tight">{item.title}</h3>
              <p className="text-sm text-emerald-950/70 mt-1">{item.description}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {(item.tags || []).slice(0, 4).map((t, i) => (
              <span key={i} className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800">{t}</span>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2 px-5">
          {[['Calories', item.calories, 'kcal'], ['Protein', item.protein, 'g'], ['Carbs', item.carbs, 'g'], ['Fat', item.fat, 'g']].map(([l, v, u]) => (
            <div key={l} className="bg-emerald-50 rounded-xl py-2 text-center">
              <div className="font-display text-base font-bold text-emerald-900">{v}{u}</div>
              <div className="text-[10px] uppercase tracking-wider text-emerald-800/60">{l}</div>
            </div>
          ))}
        </div>
        <div className="px-5 py-3 flex items-center gap-3 text-xs text-emerald-950/60 border-b border-emerald-950/5">
          <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{(item.prep_min || 0) + (item.cook_min || 0)} min</span>
          <span className="inline-flex items-center gap-1"><Flame className="w-3.5 h-3.5" />uses {(item.ingredients_used || []).length} ingredients</span>
        </div>
        <div className="px-5 py-3 flex-1 overflow-y-auto text-sm text-emerald-950/80">
          <div className="mb-3">
            <p className="text-xs uppercase tracking-widest text-emerald-950/50 mb-1">Uses</p>
            <p>{(item.ingredients_used || []).join(', ') || '—'}</p>
          </div>
          {(item.steps || []).length > 0 && (
            <button onClick={() => setShowSteps((s) => !s)} className="text-emerald-700 font-semibold text-xs uppercase tracking-widest">
              {showSteps ? 'Hide method' : 'Show method'}
            </button>
          )}
          {showSteps && (
            <ol className="mt-2 space-y-1 pl-5 list-decimal">{item.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
          )}
        </div>
        {/* Overlays */}
        <span className="pointer-events-none absolute top-6 left-6 px-3 py-1 rounded-lg border-2 border-emerald-500 text-emerald-600 font-display font-bold rotate-[-12deg] transition-opacity" style={{ opacity: likeOpacity }}>LIKE</span>
        <span className="pointer-events-none absolute top-6 right-6 px-3 py-1 rounded-lg border-2 border-rose-500 text-rose-600 font-display font-bold rotate-[12deg] transition-opacity" style={{ opacity: dislikeOpacity }}>PASS</span>
      </div>
    </div>
  );
};

const IngredientSuggestModal = ({ open, onClose }) => {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [queue, setQueue] = useState([]);   // remaining cards (top = last)
  const [liked, setLiked] = useState([]);
  const [saving, setSaving] = useState(null);

  const reset = () => { setPreview(null); setResult(null); setBusy(false); setQueue([]); setLiked([]); };

  const handleFile = (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Please choose an image under 10MB'); return; }
    const r = new FileReader();
    r.onload = (e) => { setPreview(e.target.result); setResult(null); };
    r.readAsDataURL(file);
  };

  const analyze = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const data = await suggestFromPhoto(preview);
      const items = (data.suggestions || []).slice(0, 5);
      setResult(data);
      // Store queue with the FIRST card on top (visually rendered last so highest z-index).
      setQueue(items);
      setLiked([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not analyse this photo. Try a clearer shot.');
    } finally { setBusy(false); }
  };

  const handleSwipe = async (dir) => {
    if (queue.length === 0) return;
    const top = queue[0];
    const rest = queue.slice(1);
    setQueue(rest);
    if (dir === 'like') setLiked((L) => [...L, top]);
    try { await savePreference({ action: dir, title: top.title, tags: top.tags, ingredients: top.ingredients_used }); }
    catch (e) { /* silent — preference logging is best-effort */ }
  };

  const logLikedRecipe = async (idx) => {
    const s = liked[idx];
    setSaving(idx);
    try {
      await saveMeal({
        name: s.title,
        image_base64: preview || '',
        calories: s.calories, protein: s.protein, carbs: s.carbs, fat: s.fat,
        ingredients: (s.ingredients_used || []).map((n) => ({ name: n, grams: 0, kcal: 0 })),
      });
      toast.success(`${s.title} saved to today's diary`);
    } catch (e) { toast.error('Could not save'); }
    finally { setSaving(null); }
  };

  const stackDone = result && queue.length === 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="sm:max-w-lg rounded-2xl max-h-[92vh] overflow-y-auto p-6">
        <DialogHeader>
          <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-700 grid place-items-center mb-2"><Wand2 className="w-6 h-6" /></div>
          <DialogTitle className="font-display text-2xl text-emerald-950">What can I make with this?</DialogTitle>
          <DialogDescription>Snap your fridge. Swipe right on what you'd cook, left on the rest — we'll learn your taste.</DialogDescription>
        </DialogHeader>

        {!preview ? (
          <div onClick={() => fileRef.current?.click()} className="cursor-pointer border-2 border-dashed border-emerald-200 rounded-3xl py-12 grid place-items-center hover:border-emerald-500 hover:bg-emerald-50/50 transition-colors mt-2">
            <div className="w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-700 grid place-items-center mb-3"><Camera className="w-7 h-7" /></div>
            <p className="font-display font-bold text-emerald-950">Snap or upload</p>
            <p className="text-emerald-950/60 text-sm mt-1">JPG or PNG · up to 10MB</p>
            <Button className="mt-4 bg-emerald-700 text-white rounded-full"><Upload className="w-4 h-4 mr-2" />Choose photo</Button>
          </div>
        ) : !result ? (
          <div className="mt-2">
            <div className="relative rounded-2xl overflow-hidden mb-4">
              <img src={preview} alt="ingredients" className="w-full h-56 object-cover" />
              <button onClick={reset} className="absolute top-3 right-3 bg-white/95 backdrop-blur px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5" />Change</button>
            </div>
            {!busy ? (
              <div className="text-center py-4"><Button size="lg" onClick={analyze} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full h-12 px-8"><Sparkles className="w-4 h-4 mr-2" />Suggest 5 recipes</Button></div>
            ) : (
              <div className="text-center py-8"><Loader2 className="w-8 h-8 text-emerald-600 animate-spin mx-auto mb-2" /><p className="text-emerald-950 font-display">Reading your ingredients & cooking up ideas…</p></div>
            )}
          </div>
        ) : (
          <div className="mt-2">
            {result.detected_ingredients?.length > 0 && !stackDone && (
              <div className="mb-3">
                <p className="text-xs uppercase tracking-widest text-emerald-950/50 mb-1.5">Detected</p>
                <div className="flex flex-wrap gap-1">
                  {result.detected_ingredients.slice(0, 10).map((i, idx) => (<span key={idx} className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-800">{i}</span>))}
                </div>
              </div>
            )}
            {!stackDone ? (
              <>
                <div className="relative w-full aspect-[4/5] mb-4">
                  {queue.slice(0, 3).map((item, i) => (
                    <SwipeCard key={item.title + i} item={item} index={queue.slice(0, 3).length - 1 - i} total={Math.min(3, queue.length)} onSwipe={handleSwipe} />
                  )).reverse()}
                </div>
                <div className="flex items-center justify-center gap-6">
                  <button onClick={() => handleSwipe('dislike')} className="w-14 h-14 rounded-full bg-white border-2 border-rose-200 text-rose-500 hover:bg-rose-50 grid place-items-center shadow-sm"><X className="w-6 h-6" strokeWidth={3} /></button>
                  <span className="text-xs text-emerald-950/50">{result.suggestions.length - queue.length + 1} / {result.suggestions.length}</span>
                  <button onClick={() => handleSwipe('like')} className="w-14 h-14 rounded-full bg-white border-2 border-emerald-200 text-emerald-600 hover:bg-emerald-50 grid place-items-center shadow-sm"><Heart className="w-6 h-6 fill-current" /></button>
                </div>
              </>
            ) : (
              <div>
                <div className="text-center mb-4">
                  <p className="font-display text-xl font-bold text-emerald-950">{liked.length === 0 ? 'No matches this time' : `You liked ${liked.length} recipe${liked.length === 1 ? '' : 's'}`}</p>
                  <p className="text-emerald-950/60 text-sm mt-1">We'll bias future suggestions toward what you loved.</p>
                </div>
                {liked.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {liked.map((s, idx) => (
                      <div key={idx} className="flex items-center gap-3 bg-emerald-50 rounded-2xl p-3">
                        <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 grid place-items-center shrink-0"><Heart className="w-4 h-4 fill-current" /></div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-emerald-950 truncate">{s.title}</p>
                          <p className="text-xs text-emerald-950/60">{s.calories} kcal · {s.protein}g P · {(s.prep_min || 0) + (s.cook_min || 0)}m</p>
                        </div>
                        <Button size="sm" disabled={saving === idx} onClick={() => logLikedRecipe(idx)} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full">
                          {saving === idx ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Check className="w-3.5 h-3.5 mr-1" />Log</>}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1 rounded-full border-emerald-950/10" onClick={reset}>Try another photo <ArrowRight className="w-4 h-4 ml-2" /></Button>
                </div>
              </div>
            )}
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
      </DialogContent>
    </Dialog>
  );
};

export default IngredientSuggestModal;
